/** Adapted from standalone-search f83fe2d1 via selected Registrar V2. See docs/delivery/iteration2/fusion-shared-kit/TRANSFER.json. */
import type { Column } from "@tanstack/react-table";
import { Check, ListFilter as FilterList } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface FilterOption {
  label: React.ReactNode;
  count?: number;
  value: string;
  icon?: React.ReactNode;
}

interface DataTableFacetedFilterProps<TData, TValue> {
  column: Column<TData, TValue>;
  title: string;
  scopeLabel?: string;
  single?: boolean;
  options: FilterOption[];
}

export function DataTableFacetedFilter<TData, TValue>({
  column,
  title,
  scopeLabel,
  single = false,
  options,
}: DataTableFacetedFilterProps<TData, TValue>) {
  const filterValue = (column.getFilterValue() as string[]) ?? [];
  const activeCount = filterValue.length;

  const toggle = (value: string) => {
    const next = filterValue.includes(value)
      ? filterValue.filter((v) => v !== value)
      : single ? [value] : [...filterValue, value];
    column.setFilterValue(next.length ? next : undefined);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          aria-label={activeCount ? `${title} filter, ${activeCount} selected` : `${title} filter`}
          variant="ghost"
          size="sm"
          className={cn("-ml-3", activeCount > 0 && "bg-primary-wash text-primary")}
        >
          <span>{title}</span>
          {activeCount > 0 ? (
            <span className="ml-1.5 rounded-full bg-primary-wash px-1.5 py-0.5 text-[10px] font-mono leading-none">
              {activeCount}
            </span>
          ) : (
            <FilterList className="ml-1.5 h-3.5 w-3.5 text-muted-foreground" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-48 p-1">
        {scopeLabel && (
          <p className="px-2 py-1.5 text-uiall text-muted-foreground">
            {scopeLabel}
          </p>
        )}
        <div role="group" aria-label={single ? `${title}, one at a time` : title}>
        {options.map((option) => {
          const isSelected = filterValue.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={isSelected}
              onClick={() => toggle(option.value)}
              className={cn(
                "flex min-h-[var(--control-height-compact)] items-center gap-2 w-full rounded-control px-2 text-ui hover:bg-accent cursor-pointer",
                isSelected && "bg-well",
              )}
            >
              <div
                className={cn(
                  "flex h-4 w-4 items-center justify-center rounded-control border",
                  isSelected
                    ? "bg-primary border-primary text-primary-foreground"
                    : "border-input",
                )}
              >
                {isSelected && <Check className="h-3 w-3" />}
              </div>
              {option.icon}
              <span className="flex-1 text-start">{option.label}</span>
              {option.count !== undefined && <span className="text-meta tabular-nums text-muted-foreground">{option.count}</span>}
            </button>
          );
        })}
        </div>
        {activeCount > 0 && (
          <>
            <div className="my-1 border-t" />
            <button
              type="button"
              onClick={() => column.setFilterValue(undefined)}
              className="flex min-h-[var(--control-height-compact)] w-full items-center rounded-control px-2 text-ui text-muted-foreground hover:bg-accent cursor-pointer"
            >
              Clear filter
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
